import React, { FC, useMemo, useState } from 'react';

import { DialogWithHeader, ReactPortalComponent } from '@r123/ui';

import { cn } from '@/app/_helper/tailwind';

interface Under1YearBadgeProps {
    type: 'renovation' | 'build';
    lang: 'id' | 'en';
    onClick?: () => void;
}

const Under1YearBadge: FC<Under1YearBadgeProps> = ({ lang, onClick, type }) => {
    const text = useMemo((): string => {
        if (type === 'renovation') {
            return lang === 'id' ? 'Baru Renov' : 'Newly renovated';
        }
        if (type === 'build') {
            return lang === 'id' ? 'Baru Dibangun' : 'Newly built';
        }
        return lang === 'id' ? 'Baru' : 'New';
    }, [lang, type]);

    const isGreen = type === 'renovation' || type === 'build';

    const [show, setShow] = useState(false);

    return (
        <>
            <button
                key={type}
                className={cn({
                    'flex h-5 px-1.5 py-0.5 justify-center items-center gap-1 rounded-full w-fit border-none text-white text-[10px] font-bold leading-4': true,
                    'bg-gradient-to-r from-[#398212] to-[#5FAD35]': isGreen,
                    'cursor-pointer': onClick,
                })}
                onClick={(): void => {
                    setShow(true);
                    if (onClick) onClick();
                }}
            >
                <svg
                    width='12'
                    height='12'
                    viewBox='0 0 12 12'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    className='w-3 h-3'
                >
                    <path
                        d='M7.39299 2.70679C7.51179 2.37111 7.99213 2.36094 8.13298 2.67629L8.1449 2.70698L8.30521 3.15966C8.34195 3.26348 8.40132 3.35848 8.47932 3.43825C8.55732 3.51803 8.65212 3.58073 8.75735 3.62212L8.80046 3.63766L9.26928 3.79226C9.61693 3.90697 9.62745 4.37077 9.30107 4.50677L9.26928 4.51828L8.80046 4.67307C8.6929 4.70852 8.59447 4.76584 8.51182 4.84115C8.42916 4.91646 8.36419 5.00802 8.3213 5.10964L8.30521 5.15107L8.1451 5.60395C8.0263 5.93962 7.54596 5.94979 7.40531 5.63464L7.39299 5.60395L7.23288 5.15127C7.19616 5.04741 7.1368 4.95238 7.05881 4.87256C6.98081 4.79275 6.88599 4.73002 6.78074 4.68861L6.73783 4.67307L6.26901 4.51847C5.92117 4.40377 5.91064 3.93996 6.23722 3.80415L6.26901 3.79226L6.73783 3.63766C6.84535 3.60219 6.94374 3.54486 7.02636 3.46955C7.10898 3.39424 7.17392 3.30269 7.21679 3.20109L7.23288 3.15966L7.39299 2.70679ZM9.35828 2.04541C9.39544 2.04541 9.43186 2.05548 9.4634 2.07446C9.49493 2.09345 9.52032 2.1206 9.53667 2.15283L9.5462 2.17527L9.61573 2.37207L9.81975 2.4392C9.857 2.45142 9.88964 2.47404 9.91356 2.50419C9.93748 2.53435 9.95159 2.57068 9.9541 2.60858C9.95661 2.64648 9.94741 2.68425 9.92766 2.7171C9.90792 2.74995 9.87852 2.7764 9.84319 2.7931L9.81975 2.80231L9.61593 2.86944L9.5464 3.06644C9.53373 3.10239 9.51028 3.1339 9.47904 3.15697C9.4478 3.18005 9.41017 3.19365 9.37092 3.19605C9.33167 3.19845 9.29256 3.18955 9.25855 3.17047C9.22454 3.15139 9.19716 3.123 9.17989 3.08888L9.17035 3.06644L9.10082 2.86964L8.8968 2.8025C8.85956 2.79028 8.82691 2.76766 8.80299 2.73751C8.77908 2.70736 8.76497 2.67103 8.76246 2.63312C8.75995 2.59522 8.76915 2.55745 8.78889 2.5246C8.80864 2.49175 8.83803 2.4653 8.87336 2.4486L8.8968 2.4394L9.10062 2.37226L9.17015 2.17527C9.18355 2.13737 9.20889 2.10447 9.24263 2.08118C9.27637 2.0579 9.31681 2.04539 9.35828 2.04541Z'
                        fill='white'
                    />
                    <path
                        d='M8.59087 6.17716V9.30038C8.59087 9.60162 8.34666 9.84583 8.04541 9.84583H6.68177C6.38053 9.84583 6.13632 9.60162 6.13632 9.30038V8.77031C6.13632 8.46906 5.89211 8.22486 5.59086 8.22486H5.04541C4.74416 8.22486 4.49996 8.46906 4.49996 8.77031V9.30038C4.49996 9.60162 4.25575 9.84583 3.9545 9.84583H2.59086C2.28962 9.84583 2.04541 9.60162 2.04541 9.30038V5.15179C2.04541 4.99066 2.11666 4.83777 2.24004 4.73413L4.61649 2.73797C5.02219 2.3972 5.61409 2.3972 6.01979 2.73798L6.37759 3.03852L6.05548 3.14474C6.04696 3.14755 6.03849 3.15053 6.03009 3.15368L5.9983 3.16557C5.99063 3.16844 5.98301 3.17145 5.97544 3.1746C5.07012 3.55106 5.10313 4.85194 6.05549 5.16599L6.51496 5.31751L6.53602 5.32514C6.55062 5.33159 6.56237 5.34009 6.57118 5.34911C6.58089 5.35904 6.58683 5.36941 6.59009 5.37862L6.7502 5.83131C6.75336 5.84023 6.75671 5.8491 6.76023 5.85789L6.77255 5.88858C6.77578 5.89662 6.77916 5.9046 6.78269 5.91251C7.11239 6.65129 8.10491 6.74794 8.59087 6.17716Z'
                        fill='white'
                    />
                    <path
                        d='M8.50073 4.85145C8.50073 4.85145 8.50073 4.85145 8.50073 4.85145V4.85145Z'
                        fill='white'
                    />
                </svg>

                <span>{text}</span>
            </button>

            <ReactPortalComponent>
                <DialogWithHeader
                    onClose={() => setShow(false)}
                    open={show}
                    title={lang === 'id' ? 'Baru' : 'New'}
                    className='md:w-[520px]'
                >
                    <div className='bg-white rounded-lg max-w-md mx-auto'>
                        <div className='space-y-4'>
                            <div className='flex flex-wrap gap-2'>
                                {(type === 'build' || Array.isArray(type)) && (
                                    <Under1YearBadge type='build' lang={lang} />
                                )}

                                {(type === 'renovation' ||
                                    Array.isArray(type)) && (
                                    <Under1YearBadge
                                        type='renovation'
                                        lang={lang}
                                    />
                                )}
                            </div>

                            <p className='text-gray-700 text-sm leading-relaxed'>
                                {lang === 'id'
                                    ? 'Menandakan properti yang dibangun atau direnovasi di tahun ini atau tahun lalu.'
                                    : 'Indicates properties built or renovated this year or last year.'}
                            </p>
                        </div>
                    </div>
                </DialogWithHeader>
            </ReactPortalComponent>
        </>
    );
};

export default Under1YearBadge;
